import { useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertDialog, Icon, Text } from '@hs-baumappe/legacy-ui';
import { useTranslation } from 'react-i18next';
import { MissingScopeError } from '@hs-baumappe/redkit';

import { Overtime } from '../../graphql/__generated__/OvertimeFragment';
import { OvertimeScopes } from '../../Overtime.types';
import { OvertimeLocationState } from '../Overtime.route-types';
import overtimeRoutes from '../overtimeRoutes';
import RouteDialog from '../../components/RouteDialog';
import OvertimeSignBody from './components/OvertimeSignBody';

interface OvertimeSignProps {
  overtime: Overtime;
  scopes: OvertimeScopes;
}

export default function OvertimeSign({ overtime, scopes }: OvertimeSignProps): JSX.Element {
  const { t } = useTranslation();

  const location = useLocation<OvertimeLocationState>();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(true);
  const [formDirty, setFormDirty] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  function handleDialogRequestClose() {
    if (formDirty) {
      setShowConfirmationModal(true);
      return;
    }

    setDialogOpen(false);
  }

  const handleDialogClose = useCallback(() => {
    if (location.state?.backgroundLocation) {
      navigate(location.state.backgroundLocation);
      return;
    }

    navigate(overtimeRoutes.detail.generatePath({ overtimeId: overtime.id }));
  }, [navigate, overtime.id, location.state]);

  function handleConfirmationModalConfirm() {
    setShowConfirmationModal(false);
    setDialogOpen(false);
  }

  const permitted = scopes.signOvertimeDocument.isPermitted;
  const missingScopeNames = scopes.signOvertimeDocument.missingScopeNames;

  if (!overtime.viewerCanSign) {
    return (
      <Navigate to={overtimeRoutes.detail.generatePath({ overtimeId: overtime.id })} replace />
    );
  }

  return (
    <RouteDialog
      open={dialogOpen}
      onRequestClose={handleDialogRequestClose}
      onClose={handleDialogClose}
      header={
        <>
          <Text component="h2" variant="title-small" stylex={{ marginBottom: '0' }}>
            {overtime.name}
          </Text>

          <Text variant="body-medium" color="muted">
            {t('signOvertimeModal.status', { status: t(overtime.status) })}
          </Text>
        </>
      }
    >
      {!permitted ? (
        <MissingScopeError missingScopes={missingScopeNames} />
      ) : (
        <OvertimeSignBody overtimeId={overtime.id} onFormDirty={() => setFormDirty(true)} />
      )}

      <AlertDialog
        open={showConfirmationModal}
        title={t('signOvertimeModal.closeModal.title')}
        description={t('signOvertimeModal.closeModal.content')}
        cancelButtonText={t('signOvertimeModal.closeModal.cancelButton')}
        confirmButtonText={t('signOvertimeModal.closeModal.confirmButton')}
        confirmButtonEndIcon={<Icon name="delete" />}
        onConfirm={handleConfirmationModalConfirm}
        onCancel={() => setShowConfirmationModal(false)}
        onRequestClose={() => setShowConfirmationModal(false)}
        destructive
      />
    </RouteDialog>
  );
}
